"use client";
import { dispatchSizeChangeEvent } from "@/features/product/components/ProductActions/SizeGuide/easysize";
import { usePathname } from "@/i18n/routing";
import { useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";

type ProductSize = {
  fewLeft: boolean;
  name: string;
  label: string;
  stock: boolean;
  itemId: string;
};

export type Sizes = ReturnType<typeof useSizes>["sizes"];

export const SIZE_QUERY_KEY = "size";

const useSizes = (sizes?: ProductSize[]) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const currentSelectedSizeName = searchParams.get(SIZE_QUERY_KEY);

  const selectSize = useCallback(
    (size: ProductSize, notifyEasySize = false) => {
      if (notifyEasySize) {
        dispatchSizeChangeEvent(size.name);
      }

      const newParams = new URLSearchParams(searchParams);
      newParams.set(SIZE_QUERY_KEY, size.name);

      window.history.replaceState(
        {},
        "",
        `${pathname}?${newParams.toString()}`,
      );
    },
    [pathname, searchParams],
  );

  const formattedSizes = useMemo(
    () =>
      sizes?.map(size => {
        return {
          ...size,
          isSelected: currentSelectedSizeName === size.name,
          selectSize: (notify = false) => selectSize(size, notify),
        };
      }),
    [sizes, currentSelectedSizeName, selectSize],
  );

  const selectedSize = useMemo(() => {
    return currentSelectedSizeName !== null
      ? sizes?.find(size => size.name === currentSelectedSizeName)
      : sizes?.length === 1
        ? sizes?.[0]
        : undefined;
  }, [sizes, currentSelectedSizeName]);

  return {
    sizes: formattedSizes,
    selectedSize: selectedSize,
  };
};

export default useSizes;
